import { logger } from "./logger";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { EVENTS } from "@/lib/events";

/**
 * Admin-config server functions: tenants, customers, facilities, carriers,
 * consumables, putaway rules, fulfillment settings, zones, totes, audit log,
 * and print queue management.
 *
 * All handlers run with the authenticated user's Supabase client so existing
 * RLS policies enforce tenant/role boundaries. State changes write to
 * `audit_events` using the centralized `EVENTS` registry.
 */

async function writeAudit(opts: {
  supabase: any;
  tenantId: string;
  actorId: string;
  entityType: string;
  entityId: string;
  action: string;
  diff?: Record<string, unknown>;
}) {
  try {
    await opts.supabase.from("audit_events").insert({
      tenant_id: opts.tenantId,
      actor_id: opts.actorId,
      entity_type: opts.entityType,
      entity_id: opts.entityId,
      action: opts.action,
      diff: opts.diff ?? null,
    });
  } catch (err) {
    logger.error("audit failed to record", err, { action: opts.action });
  }
}

const Uuid = z.string().uuid();

// ---------- Tenants ----------

export const listTenants = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("tenants")
      .select("id, name, gs1_prefix, status, created_at")
      .eq("is_test", false)
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return data ?? [];
  });

export const listTenantsOptions = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("tenants")
      .select("id, name")
      .eq("is_test", false)
      .order("name");
    if (error) throw new Error(error.message);
    return data ?? [];
  });

const CreateTenantInput = z.object({
  name: z.string().min(1).max(255),
  gs1Prefix: z.string().max(32).regex(/^[0-9]*$/).nullable().optional(),
});

export const createTenant = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => CreateTenantInput.parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: row, error } = await supabase.from("tenants").insert({
      name: data.name.trim(),
      gs1_prefix: data.gs1Prefix?.trim() || null,
    }).select("id, tenant_id:id").single();
    if (error || !row) throw new Error(error?.message ?? "E_VALIDATION: create tenant");
    await writeAudit({
      supabase, tenantId: row.id as string, actorId: userId,
      entityType: "tenant", entityId: row.id as string,
      action: EVENTS.TENANT_CREATED, diff: { name: data.name },
    });
    return { id: row.id as string };
  });

// ---------- Customers ----------

export const listCustomers = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("customers")
      .select("id, name, tenant_id, contact_info, created_at, gs1_prefix, default_carrier, default_service, enabled_pick_strategies, default_pick_strategy, enabled_pack_strategies, default_pack_strategy, in_common_overlap_threshold, in_common_max_orders")
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return data ?? [];
  });

export const listCustomersOptions = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("customers").select("id, name, tenant_id").order("name");
    if (error) throw new Error(error.message);
    return data ?? [];
  });

const Address = z.object({
  line1: z.string().max(255).optional(),
  line2: z.string().max(255).optional(),
  city: z.string().max(128).optional(),
  region: z.string().max(128).optional(),
  postal_code: z.string().max(32).optional(),
  country: z.string().max(2).optional(),
}).strict();

const Addresses = z.object({
  ship_from: Address.optional(),
  return_to: Address.optional(),
  business_physical: Address.optional(),
}).strict();

const ContactInfo = z.object({
  email: z.string().email().max(255).optional(),
  phone: z.string().max(64).optional(),
  addresses: Addresses.optional(),
}).strict();

const CreateCustomerInput = z.object({
  tenantId: Uuid,
  name: z.string().min(1).max(255),
  contactInfo: ContactInfo,
});

export const createCustomer = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => CreateCustomerInput.parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: row, error } = await supabase.from("customers").insert({
      tenant_id: data.tenantId,
      name: data.name.trim(),
      contact_info: data.contactInfo,
      external_code: `${data.name.trim().toUpperCase().replace(/[^A-Z0-9]+/g, "_")}_${Date.now().toString(36).toUpperCase()}`,
    }).select("id").single();
    if (error || !row) throw new Error(error?.message ?? "E_VALIDATION: create customer");
    await writeAudit({
      supabase, tenantId: data.tenantId, actorId: userId,
      entityType: "customer", entityId: row.id as string,
      action: EVENTS.CUSTOMER_CREATED, diff: { name: data.name },
    });
    return { id: row.id as string };
  });

const UpdateCustomerInput = z.object({
  id: Uuid,
  name: z.string().min(1).max(255),
  contactInfo: ContactInfo,
});

export const updateCustomer = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => UpdateCustomerInput.parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: row, error } = await supabase.from("customers")
      .update({ name: data.name.trim(), contact_info: data.contactInfo })
      .eq("id", data.id)
      .select("tenant_id").single();
    if (error || !row) throw new Error(error?.message ?? "E_NOT_FOUND: customer");
    await writeAudit({
      supabase, tenantId: row.tenant_id as string, actorId: userId,
      entityType: "customer", entityId: data.id,
      action: EVENTS.CUSTOMER_UPDATED, diff: { name: data.name },
    });
    return { ok: true };
  });

const PickStratEnum = z.enum(["discrete", "pick_to_bin", "batch", "zone", "wave"]);
const PackStratEnum = z.enum(["discrete", "bulk", "scan_verify"]);

const UpdateCustomerConfigInput = z.object({
  id: Uuid,
  gs1Prefix: z.string().max(32).regex(/^[0-9]*$/).nullable().optional(),
  defaultCarrier: z.string().max(64).nullable().optional(),
  defaultService: z.string().max(64).nullable().optional(),
  enabledPickStrategies: z.array(PickStratEnum).max(10).optional(),
  defaultPickStrategy: PickStratEnum.nullable().optional(),
  enabledPackStrategies: z.array(PackStratEnum).max(10).optional(),
  defaultPackStrategy: PackStratEnum.nullable().optional(),
  inCommonOverlapThreshold: z.number().min(0).max(1).optional(),
  inCommonMaxOrders: z.number().int().min(2).max(500).optional(),
  packingSlipEnabled: z.boolean().optional(),
});

export const updateCustomerConfig = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => UpdateCustomerConfigInput.parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const patch: Record<string, any> = {};
    if (data.gs1Prefix !== undefined) patch.gs1_prefix = data.gs1Prefix?.trim() || null;
    if (data.defaultCarrier !== undefined) patch.default_carrier = data.defaultCarrier?.trim() || null;
    if (data.defaultService !== undefined) patch.default_service = data.defaultService?.trim() || null;
    if (data.enabledPickStrategies !== undefined) patch.enabled_pick_strategies = data.enabledPickStrategies;
    if (data.defaultPickStrategy !== undefined) patch.default_pick_strategy = data.defaultPickStrategy;
    if (data.enabledPackStrategies !== undefined) patch.enabled_pack_strategies = data.enabledPackStrategies;
    if (data.defaultPackStrategy !== undefined) patch.default_pack_strategy = data.defaultPackStrategy;
    if (data.inCommonOverlapThreshold !== undefined) patch.in_common_overlap_threshold = data.inCommonOverlapThreshold;
    if (data.inCommonMaxOrders !== undefined) patch.in_common_max_orders = data.inCommonMaxOrders;
    if (data.packingSlipEnabled !== undefined) patch.packing_slip_enabled = data.packingSlipEnabled;
    const { data: row, error } = await supabase.from("customers")
      .update(patch as never).eq("id", data.id).select("tenant_id").single();
    if (error || !row) throw new Error(error?.message ?? "E_NOT_FOUND: customer");
    await writeAudit({
      supabase, tenantId: row.tenant_id as string, actorId: userId,
      entityType: "customer", entityId: data.id,
      action: EVENTS.CUSTOMER_UPDATED, diff: patch,
    });
    return { ok: true };
  });

// ---------- Facilities ----------

export const listFacilities = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("facilities")
      .select("id, name, tenant_id, timezone, created_at")
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return data ?? [];
  });

const CreateFacilityInput = z.object({
  tenantId: Uuid,
  name: z.string().min(1).max(255),
  timezone: z.string().min(1).max(64),
});

export const createFacility = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => CreateFacilityInput.parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: row, error } = await supabase.from("facilities").insert({
      tenant_id: data.tenantId,
      name: data.name.trim(),
      timezone: data.timezone || "UTC",
      code: `${data.name.trim().toUpperCase().replace(/[^A-Z0-9]+/g, "_")}_${Date.now().toString(36).toUpperCase()}`,
    }).select("id").single();
    if (error || !row) throw new Error(error?.message ?? "E_VALIDATION: create facility");
    await writeAudit({
      supabase, tenantId: data.tenantId, actorId: userId,
      entityType: "facility", entityId: row.id as string,
      action: EVENTS.FACILITY_CREATED, diff: { name: data.name },
    });
    return { id: row.id as string };
  });

// ---------- Carriers ----------

const TenantInput = z.object({ tenantId: Uuid });

const ListCarriersInput = z.object({
  tenantId: Uuid,
  customerId: Uuid.nullable().optional(),
});

export const listCarriers = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => ListCarriersInput.parse(input))
  .handler(async ({ data, context }) => {
    let q = context.supabase
      .from("carriers")
      .select("id, code, name, tracking_url_template, active, customer_id, meta")
      .eq("tenant_id", data.tenantId)
      .order("code");
    if (data.customerId) {
      // customer-specific OR tenant-wide fallback
      q = q.or(`customer_id.eq.${data.customerId},customer_id.is.null`);
    }
    const { data: carriers, error } = await q;
    if (error) throw new Error(error.message);
    const ids = (carriers ?? []).map((c: any) => c.id);
    let services: any[] = [];
    if (ids.length > 0) {
      const { data: svc, error: e2 } = await context.supabase
        .from("carrier_services")
        .select("id, carrier_id, code, name, transit_days, active")
        .in("carrier_id", ids)
        .order("code");
      if (e2) throw new Error(e2.message);
      services = svc ?? [];
    }
    return { carriers: carriers ?? [], services };
  });

const CreateCarrierInput = z.object({
  tenantId: Uuid,
  customerId: Uuid.nullable().optional(),
  code: z.string().min(1).max(32).regex(/^[A-Za-z0-9_-]+$/),
  name: z.string().min(1).max(255),
  trackingUrlTemplate: z.string().url().max(512).nullable().optional(),
  shipFromAddressKey: z.enum(["ship_from", "return_to", "business_physical"]).nullable().optional(),
});

export const createCarrier = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => CreateCarrierInput.parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: row, error } = await supabase.from("carriers").insert({
      tenant_id: data.tenantId,
      customer_id: data.customerId ?? null,
      code: data.code.trim(),
      name: data.name.trim(),
      tracking_url_template: data.trackingUrlTemplate?.trim() || null,
      meta: data.shipFromAddressKey ? { ship_from_address_key: data.shipFromAddressKey } : {},
    }).select("id").single();
    if (error || !row) throw new Error(error?.message ?? "E_VALIDATION: create carrier");
    await writeAudit({
      supabase, tenantId: data.tenantId, actorId: userId,
      entityType: "carrier", entityId: row.id as string,
      action: EVENTS.CARRIER_CREATED, diff: { code: data.code },
    });
    return { id: row.id as string };
  });

const ToggleCarrierInput = z.object({ id: Uuid, active: z.boolean() });

export const toggleCarrier = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => ToggleCarrierInput.parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: row, error } = await supabase.from("carriers")
      .update({ active: data.active })
      .eq("id", data.id)
      .select("tenant_id").single();
    if (error || !row) throw new Error(error?.message ?? "E_NOT_FOUND: carrier");
    await writeAudit({
      supabase, tenantId: row.tenant_id as string, actorId: userId,
      entityType: "carrier", entityId: data.id,
      action: EVENTS.CARRIER_TOGGLED, diff: { active: data.active },
    });
    return { ok: true };
  });

const CreateCarrierServiceInput = z.object({
  carrierId: Uuid,
  code: z.string().min(1).max(32).regex(/^[A-Za-z0-9_-]+$/),
  name: z.string().min(1).max(255),
  transitDays: z.number().int().min(0).max(365).nullable().optional(),
});

export const createCarrierService = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => CreateCarrierServiceInput.parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: carrier, error: ce } = await supabase
      .from("carriers").select("tenant_id").eq("id", data.carrierId).single();
    if (ce || !carrier) throw new Error("E_NOT_FOUND: carrier");
    const { data: row, error } = await supabase.from("carrier_services").insert({
      carrier_id: data.carrierId,
      code: data.code.trim(),
      name: data.name.trim(),
      transit_days: data.transitDays ?? null,
    }).select("id").single();
    if (error || !row) throw new Error(error?.message ?? "E_VALIDATION: create service");
    await writeAudit({
      supabase, tenantId: carrier.tenant_id as string, actorId: userId,
      entityType: "carrier_service", entityId: row.id as string,
      action: EVENTS.CARRIER_SERVICE_CREATED, diff: { code: data.code, carrier_id: data.carrierId },
    });
    return { id: row.id as string };
  });

export const deleteCarrierService = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ id: Uuid }).parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: svc } = await supabase.from("carrier_services")
      .select("carrier_id, carriers(tenant_id)").eq("id", data.id).single();
    const { error } = await supabase.from("carrier_services").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    const tenantId = (svc as any)?.carriers?.tenant_id as string | undefined;
    if (tenantId) {
      await writeAudit({
        supabase, tenantId, actorId: userId,
        entityType: "carrier_service", entityId: data.id,
        action: EVENTS.CARRIER_SERVICE_DELETED,
      });
    }
    return { ok: true };
  });

// ---------- Consumables ----------

export const listConsumables = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => TenantInput.parse(input))
  .handler(async ({ data, context }) => {
    const { data: rows, error } = await context.supabase
      .from("consumables")
      .select("id, code, name, category, unit_cost_cents, active, created_at")
      .eq("tenant_id", data.tenantId)
      .order("code");
    if (error) throw new Error(error.message);
    return rows ?? [];
  });

const CreateConsumableInput = z.object({
  tenantId: Uuid,
  code: z.string().min(1).max(64).regex(/^[A-Za-z0-9_-]+$/),
  name: z.string().min(1).max(255),
  category: z.enum(["box", "mailer", "pallet", "dunnage", "label", "other"]),
  unitCostCents: z.number().int().min(0).max(10_000_000),
});

export const createConsumable = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => CreateConsumableInput.parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: row, error } = await supabase.from("consumables").insert({
      tenant_id: data.tenantId,
      code: data.code.trim(),
      name: data.name.trim(),
      category: data.category,
      unit_cost_cents: data.unitCostCents,
    }).select("id").single();
    if (error || !row) throw new Error(error?.message ?? "E_VALIDATION: create consumable");
    await writeAudit({
      supabase, tenantId: data.tenantId, actorId: userId,
      entityType: "consumable", entityId: row.id as string,
      action: EVENTS.CONSUMABLE_CREATED, diff: { code: data.code },
    });
    return { id: row.id as string };
  });

export const toggleConsumable = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ id: Uuid, active: z.boolean() }).parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: row, error } = await supabase.from("consumables")
      .update({ active: data.active }).eq("id", data.id)
      .select("tenant_id").single();
    if (error || !row) throw new Error(error?.message ?? "E_NOT_FOUND: consumable");
    await writeAudit({
      supabase, tenantId: row.tenant_id as string, actorId: userId,
      entityType: "consumable", entityId: data.id,
      action: EVENTS.CONSUMABLE_TOGGLED, diff: { active: data.active },
    });
    return { ok: true };
  });

// ---------- Putaway rules ----------

export const listPutawayRules = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => TenantInput.parse(input))
  .handler(async ({ data, context }) => {
    const { data: rows, error } = await context.supabase
      .from("putaway_rules")
      .select("id, customer_id, sku_id, zone, location_id, priority, active, notes")
      .eq("tenant_id", data.tenantId)
      .order("priority");
    if (error) throw new Error(error.message);
    return rows ?? [];
  });

const PutawayRefsInput = TenantInput;

export const getPutawayRefs = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => PutawayRefsInput.parse(input))
  .handler(async ({ data, context }) => {
    const sb = context.supabase;
    const [c, s, l] = await Promise.all([
      sb.from("customers").select("id, name").eq("tenant_id", data.tenantId).order("name"),
      sb.from("skus").select("id, sku_code, name").eq("tenant_id", data.tenantId).order("sku_code").limit(500),
      sb.from("locations").select("id, code, zone").eq("tenant_id", data.tenantId).eq("active", true).order("code"),
    ]);
    return {
      customers: c.data ?? [],
      skus: s.data ?? [],
      locations: l.data ?? [],
    };
  });

const CreatePutawayInput = z.object({
  tenantId: Uuid,
  customerId: Uuid.nullable().optional(),
  skuId: Uuid.nullable().optional(),
  zone: z.string().max(32).nullable().optional(),
  locationId: Uuid.nullable().optional(),
  priority: z.number().int().min(0).max(100000),
  notes: z.string().max(512).nullable().optional(),
}).refine((v) => !!(v.customerId || v.skuId || v.zone || v.locationId), {
  message: "Pick at least one match",
});

export const createPutawayRule = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => CreatePutawayInput.parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: row, error } = await supabase.from("putaway_rules").insert({
      tenant_id: data.tenantId,
      customer_id: data.customerId || null,
      sku_id: data.skuId || null,
      zone: data.zone?.trim() || null,
      location_id: data.locationId || null,
      priority: data.priority,
      notes: data.notes?.trim() || null,
    }).select("id").single();
    if (error || !row) throw new Error(error?.message ?? "E_VALIDATION: create rule");
    await writeAudit({
      supabase, tenantId: data.tenantId, actorId: userId,
      entityType: "putaway_rule", entityId: row.id as string,
      action: EVENTS.PUTAWAY_RULE_CREATED,
    });
    return { id: row.id as string };
  });

export const togglePutawayRule = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ id: Uuid, active: z.boolean() }).parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: row, error } = await supabase.from("putaway_rules")
      .update({ active: data.active }).eq("id", data.id)
      .select("tenant_id").single();
    if (error || !row) throw new Error(error?.message ?? "E_NOT_FOUND: rule");
    await writeAudit({
      supabase, tenantId: row.tenant_id as string, actorId: userId,
      entityType: "putaway_rule", entityId: data.id,
      action: EVENTS.PUTAWAY_RULE_UPDATED, diff: { active: data.active },
    });
    return { ok: true };
  });

export const deletePutawayRule = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ id: Uuid }).parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: row } = await supabase.from("putaway_rules")
      .select("tenant_id").eq("id", data.id).single();
    const { error } = await supabase.from("putaway_rules").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    if (row?.tenant_id) {
      await writeAudit({
        supabase, tenantId: row.tenant_id as string, actorId: userId,
        entityType: "putaway_rule", entityId: data.id,
        action: EVENTS.PUTAWAY_RULE_DELETED,
      });
    }
    return { ok: true };
  });

// ---------- Fulfillment settings / zones / totes ----------

const FacilityInput = z.object({
  tenantId: Uuid,
  facilityId: Uuid,
});

export const getOrCreateFulfillmentSettings = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => FacilityInput.parse(input))
  .handler(async ({ data, context }) => {
    const { supabase } = context;
    const { data: existing } = await supabase
      .from("fulfillment_settings").select("*")
      .eq("facility_id", data.facilityId).maybeSingle();
    if (existing) return existing;
    const { data: created, error } = await supabase
      .from("fulfillment_settings")
      .insert({ tenant_id: data.tenantId, facility_id: data.facilityId })
      .select("*").single();
    if (error || !created) throw new Error(error?.message ?? "E_VALIDATION: settings");
    return created;
  });

const UpdateFulfillmentSettingsInput = z.object({
  id: Uuid,
  patch: z.record(z.string(), z.any()),
});

export const updateFulfillmentSettings = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => UpdateFulfillmentSettingsInput.parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: row, error } = await supabase.from("fulfillment_settings")
      .update(data.patch as never).eq("id", data.id)
      .select("tenant_id").single();
    if (error || !row) throw new Error(error?.message ?? "E_NOT_FOUND: settings");
    await writeAudit({
      supabase, tenantId: row.tenant_id as string, actorId: userId,
      entityType: "fulfillment_settings", entityId: data.id,
      action: EVENTS.FULFILLMENT_SETTINGS_UPDATED, diff: data.patch,
    });
    return { ok: true };
  });

export const listZones = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ facilityId: Uuid }).parse(input))
  .handler(async ({ data, context }) => {
    const { data: rows, error } = await context.supabase
      .from("zones").select("*").eq("facility_id", data.facilityId).order("pick_sequence");
    if (error) throw new Error(error.message);
    return rows ?? [];
  });

const CreateZoneInput = z.object({
  tenantId: Uuid,
  facilityId: Uuid,
  code: z.string().min(1).max(16).regex(/^[A-Za-z0-9_-]+$/),
  name: z.string().min(1).max(255),
  pickSequence: z.number().int().min(0).max(100000),
});

export const createZone = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => CreateZoneInput.parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: row, error } = await supabase.from("zones").insert({
      tenant_id: data.tenantId,
      facility_id: data.facilityId,
      code: data.code.trim().toUpperCase(),
      name: data.name.trim(),
      pick_sequence: data.pickSequence,
    }).select("id").single();
    if (error || !row) throw new Error(error?.message ?? "E_VALIDATION: zone");
    await writeAudit({
      supabase, tenantId: data.tenantId, actorId: userId,
      entityType: "zone", entityId: row.id as string,
      action: EVENTS.ZONE_CREATED, diff: { code: data.code },
    });
    return { id: row.id as string };
  });

export const deleteZone = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ id: Uuid }).parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: row } = await supabase.from("zones").select("tenant_id").eq("id", data.id).single();
    const { error } = await supabase.from("zones").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    if (row?.tenant_id) {
      await writeAudit({
        supabase, tenantId: row.tenant_id as string, actorId: userId,
        entityType: "zone", entityId: data.id, action: EVENTS.ZONE_DELETED,
      });
    }
    return { ok: true };
  });

export const listTotes = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ facilityId: Uuid }).parse(input))
  .handler(async ({ data, context }) => {
    const { data: rows, error } = await context.supabase
      .from("totes").select("*").eq("facility_id", data.facilityId).order("code");
    if (error) throw new Error(error.message);
    return rows ?? [];
  });

const CreateTotesInput = z.object({
  tenantId: Uuid,
  facilityId: Uuid,
  codes: z.array(z.string().min(1).max(64).regex(/^[A-Za-z0-9_-]+$/)).min(1).max(500),
});

export const createTotes = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => CreateTotesInput.parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const rows = data.codes.map((code) => ({
      tenant_id: data.tenantId,
      facility_id: data.facilityId,
      code: code.trim().toUpperCase(),
    }));
    const { data: inserted, error } = await supabase.from("totes").insert(rows).select("id");
    if (error) throw new Error(error.message);
    await writeAudit({
      supabase, tenantId: data.tenantId, actorId: userId,
      entityType: "tote", entityId: data.facilityId,
      action: EVENTS.TOTE_CREATED, diff: { count: rows.length },
    });
    return { count: inserted?.length ?? 0 };
  });

export const deleteTote = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ id: Uuid }).parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: row } = await supabase.from("totes").select("tenant_id").eq("id", data.id).single();
    const { error } = await supabase.from("totes").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    if (row?.tenant_id) {
      await writeAudit({
        supabase, tenantId: row.tenant_id as string, actorId: userId,
        entityType: "tote", entityId: data.id, action: EVENTS.TOTE_DELETED,
      });
    }
    return { ok: true };
  });

// ---------- Audit log ----------

const ListAuditInput = z.object({
  tenantId: Uuid,
  entity: z.string().min(1).max(64).optional(),
  action: z.string().min(1).max(80).optional(),
  entityId: z.string().min(1).max(128).optional(),
  fromIso: z.string().datetime().optional(),
  toIso: z.string().datetime().optional(),
  search: z.string().max(200).optional(),
  limit: z.number().int().min(1).max(2000).optional(),
});

export const listAuditEvents = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => ListAuditInput.parse(input))
  .handler(async ({ data, context }) => {
    let q = context.supabase.from("audit_events")
      .select("id, actor_id, entity_type, entity_id, action, diff, created_at")
      .eq("tenant_id", data.tenantId)
      .order("created_at", { ascending: false })
      .limit(data.limit ?? 500);
    if (data.entity && data.entity !== "all") q = q.eq("entity_type", data.entity);
    if (data.action && data.action !== "all") q = q.eq("action", data.action);
    if (data.entityId) q = q.eq("entity_id", data.entityId);
    if (data.fromIso) q = q.gte("created_at", data.fromIso);
    if (data.toIso) q = q.lte("created_at", data.toIso);
    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);
    const events = (rows ?? []) as any[];
    // Optional client-style search across action/entity_id/actor_id.
    const s = data.search?.trim().toLowerCase();
    if (!s) return events;
    return events.filter((e) =>
      (e.action ?? "").toLowerCase().includes(s)
      || (e.entity_id ?? "").toLowerCase().includes(s)
      || (e.actor_id ?? "").toLowerCase().includes(s),
    );
  });

// ---------- Print queue ----------

const ListPrintJobsInput = z.object({
  tenantId: Uuid,
  status: z.enum(["queued", "sent", "printed", "failed", "cancelled", "all"]),
  fromIso: z.string().datetime().optional(),
  toIso: z.string().datetime().optional(),
  sort: z.enum(["asc", "desc"]).optional(),
});

export const listPrintJobs = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => ListPrintJobsInput.parse(input))
  .handler(async ({ data, context }) => {
    const ascending = data.sort === "asc";
    let q = context.supabase
      .from("print_jobs")
      .select("id, kind, format, status, copies, printer_name, error, retries, created_at, printed_at, payload")
      .eq("tenant_id", data.tenantId)
      .order("created_at", { ascending })
      .limit(200);
    if (data.status !== "all") q = q.eq("status", data.status);
    if (data.fromIso) q = q.gte("created_at", data.fromIso);
    if (data.toIso) q = q.lte("created_at", data.toIso);
    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);
    return rows ?? [];
  });

const SetPrintStatusInput = z.object({
  id: Uuid,
  status: z.enum(["queued", "sent", "printed", "failed", "cancelled"]),
});

export const setPrintJobStatus = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => SetPrintStatusInput.parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const patch: { status: typeof data.status; printed_at?: string | null } = { status: data.status };
    if (data.status === "printed") patch.printed_at = new Date().toISOString();
    if (data.status === "queued") patch.printed_at = null;
    const { data: row, error } = await supabase.from("print_jobs")
      .update(patch).eq("id", data.id)
      .select("tenant_id").single();
    if (error || !row) throw new Error(error?.message ?? "E_NOT_FOUND: job");
    await writeAudit({
      supabase, tenantId: row.tenant_id as string, actorId: userId,
      entityType: "print_job", entityId: data.id,
      action: EVENTS.PRINT_JOB_STATUS_CHANGED, diff: { status: data.status },
    });
    return { ok: true };
  });

// ---------- Notifications (alerts admin) ----------

const ListNotificationsAdminInput = z.object({
  tenantId: Uuid,
  fromIso: z.string().datetime().optional(),
  toIso: z.string().datetime().optional(),
  sort: z.enum(["asc", "desc"]).optional(),
});

export const listNotificationsAdmin = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => ListNotificationsAdminInput.parse(input))
  .handler(async ({ data, context }) => {
    const ascending = data.sort === "asc";
    let q = context.supabase
      .from("notifications")
      .select("id, kind, severity, title, body, created_at, read_at, link")
      .eq("tenant_id", data.tenantId)
      .order("created_at", { ascending })
      .limit(100);
    if (data.fromIso) q = q.gte("created_at", data.fromIso);
    if (data.toIso) q = q.lte("created_at", data.toIso);
    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);
    return rows ?? [];
  });
